import { useEffect } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { brl } from "@/lib/pricing";

const AnimatedTotal = ({ value }) => {
  const mv = useMotionValue(value);
  const text = useTransform(mv, (v) => brl(Math.round(v)));
  useEffect(() => {
    const c = animate(mv, value, { duration: 0.8, ease: [0.22, 1, 0.36, 1] });
    return () => c.stop();
  }, [value, mv]);
  return <motion.span data-testid="summary-total">{text}</motion.span>;
};

const Row = ({ label, value, testid, accent }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm">
    <span className="text-[#d9d4c9]">{label}</span>
    <span data-testid={testid} className={`text-right font-bold ${accent ? "text-[#f0b48f]" : "text-[#faf7f2]"}`}>{value}</span>
  </div>
);

const fmt = (d) => (d ? format(d, "dd MMM yyyy", { locale: ptBR }) : "—");

export default function Summary({ chalet, range, quote, deco, form, loading, onSubmit }) {
  const card = form.forma_pagamento === "cartao";
  return (
    <aside data-testid="booking-summary" className="grain relative overflow-hidden rounded-sm bg-[#163323] p-6 text-[#faf7f2] sm:p-8">
      <div className="relative z-10">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#e9c9a8]">Resumo da reserva</p>
        <h3 data-testid="summary-chalet" className="mt-3 text-2xl font-bold uppercase tracking-tight">{chalet.name}</h3>
        <div className="mt-5 grid grid-cols-2 gap-3 border-y border-[#2c4a38] py-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#b8c4bb]">Entrada</p>
            <p data-testid="summary-checkin" className="mt-1 font-bold">{fmt(range?.from)}</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#b8c4bb]">Saída</p>
            <p data-testid="summary-checkout" className="mt-1 font-bold">{fmt(range?.to)}</p>
          </div>
        </div>
        <div className="mt-3 divide-y divide-[#2c4a38]/70">
          <Row label={`${quote.nights} diária${quote.nights === 1 ? "" : "s"} × ${brl(chalet.price)}`} value={brl(quote.base)} testid="summary-base" />
          {quote.peakNights > 0 && <Row label={`Datas especiais (${quote.peakNights} × R$ 500,00)`} value={`+ ${brl(quote.peakTotal)}`} testid="summary-peak" accent />}
          {quote.petTotal > 0 && <Row label="Pet" value={`+ ${brl(quote.petTotal)}`} testid="summary-pet" />}
          {deco && <Row label={`Decoração ${deco.name}`} value={`+ ${brl(deco.price)}`} testid="summary-deco" />}
        </div>
        <div className="mt-5 border-t border-[#faf7f2]/30 pt-5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#b8c4bb]">Valor total</p>
          <p className="mt-1 text-4xl font-bold tracking-tight sm:text-5xl"><AnimatedTotal value={quote.total} /></p>
          <p data-testid="summary-payment" className="mt-2 text-sm text-[#d9d4c9]">
            {card ? `Cartão em ${form.parcelas}x de ${brl(quote.total / form.parcelas)} sem juros` : form.forma_pagamento === "pix" ? "Pagamento via Pix" : "Escolha a forma de pagamento"}
          </p>
        </div>
        <button
          type="button"
          data-testid="submit-reservation-button"
          onClick={onSubmit}
          disabled={loading}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#c86d4b] px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#a4532f] disabled:opacity-70"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Enviando..." : "Confirmar solicitação de reserva"}
        </button>
        <p className="mt-3 text-center text-xs text-[#b8c4bb]">Sem pagamento online. Entraremos em contato pelo WhatsApp.</p>
      </div>
    </aside>
  );
}
